import Claim from '@/models/Claim';
import LumpSumCost from '@/models/LumpSumCost';
import RatesCost from '@/models/RatesCost';
import VariationOrderCost from '@/models/VariationOrderCost';
import ClaimsService from '@/services/ClaimsService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useClaimsStore = defineStore('claims', {
  state: () => ({}),

  getters: {
    models: () => useRepo(Claim),
  },

  actions: {
    async fetchClaims(projectId: Id, form: ApiRequests['api.v1.claims.index']) {
      const [data, error] = await ClaimsService.index(projectId, form);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async createClaim(projectId: Id, request: ApiRequests['api.v1.claims.store']) {
      const [data, error] = await ClaimsService.create(projectId, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateClaim(id: Id, request: ApiRequests['api.v1.claims.update']) {
      const [data, error] = await ClaimsService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteClaim(id: Id) {
      const [data, error] = await ClaimsService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async fetchClaim(id: Id) {
      const [data, error] = await ClaimsService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async sendClaim(id: Id, request: ApiRequests['api.v1.claims.send.draft']) {
      const [data, error] = await ClaimsService.send(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async requestApproval(id: Id, request: ApiRequests['api.v1.claims.request-approval'] = {}) {
      const [data, error] = await ClaimsService.requestApproval(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async sendContractor(id: Id, signedParams: unknown, request: ApiRequests['api.v1.claims.send.contractor']) {
      const [data, error] = await ClaimsService.sendContractor(id, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async sendPqs(id: Id, signedParams: unknown, request: ApiRequests['api.v1.claims.send.pqs']) {
      const [data, error] = await ClaimsService.sendPqs(id, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async submit(id: Id, signedParams: unknown, request: ApiRequests['api.v1.claims.certify']) {
      const [data, error] = await ClaimsService.submit(id, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async export(id: Id, form: ApiRequests['api.v1.claims.lite.export'] = {}) {
      const [data, error] = await ClaimsService.export(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async exportRetentionRelease(projectId: Id, form: ApiRequests['api.v1.projects.export-retention-release']) {
      const [data, error] = await ClaimsService.exportRetentionRelease(projectId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async exportFinalAccount(projectId: Id) {
      const [data, error] = await ClaimsService.exportFinalAccount(projectId);

      if(error) {
        throw error;
      }

      return data;
    },

    async certifyLite(id: Id, request: ApiRequests['api.v1.claims.lite.certify']) {
      const [data, error] = await ClaimsService.certifyLite(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async uncertify(id: Id) {
      const [data, error] = await ClaimsService.uncertify(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchAdminIndex(page = 1) {
      const [data, error] = await ClaimsService.admin.index(page);

      if(error) {
        throw error;
      }

      this.models.flush();
      this.models.save(data.claims.data);

      return data;
    },

    async fetchAdminClaim(id: Id) {
      const [data, error] = await ClaimsService.admin.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateAdminClaim(id: Id, request: ApiRequests['api.v1.admin.claims.update']) {
      const [data, error] = await ClaimsService.admin.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteAdminClaim(id: Id) {
      const [data, error] = await ClaimsService.admin.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async storeMedia(id: Id, request: ApiRequests['api.v1.claims.store-media'] | FormData) {
      const [data, error] = await ClaimsService.storeMedia(id, request);

      if(error) {
        throw error;
      }

      return data;
    },

    async media(id: Id) {
      const [data, error] = await ClaimsService.media(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeSubcontractorClaim(
      projectId: Id,
      projectSupplierId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.store'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.store(projectId, projectSupplierId, request);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async issueSubcontractorClaim(
      projectId: Id,
      claimId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.issue'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.issue(projectId, claimId, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async submitSubcontractorClaim(
      id: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.submit'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.submit(id, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateSubcontractorClaimSopaDisclaimer(
      id: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.sopa-disclaimer.update'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.updateSopaDisclaimer(id, signedParams, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateSubcontractorClaimSopaDisclaimerLite(
      id: Id,
      request: ApiRequests['api.v1.subcontractor.claims.lite.sopa-disclaimer.update'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.updateSopaDisclaimerLite(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async showSubcontractorClaim(projectId: Id, claimId: Id) {
      const [data, error] = await ClaimsService.subcontractor.show(projectId, claimId);

      if(error) {
        throw error;
      }

      this.models.save(data.data);

      return data;
    },

    async fetchSubcontractorClaimsSummary(projectId: Id, claimId: Id) {
      const lumpSumRepo = useRepo(LumpSumCost);
      const ratesCostRepo = useRepo(RatesCost);
      const variationOrderCostRepo = useRepo(VariationOrderCost);
      const [data, error] = await ClaimsService.subcontractor.summary(projectId, claimId);

      if(error) {
        throw error;
      }

      lumpSumRepo.save(data.lump_sums);
      ratesCostRepo.save(data.rates);
      variationOrderCostRepo.save(data.variation_orders);

      return data;
    },

    async certifySubcontractorClaim(
      projectId: Id,
      id: Id,
      request: ApiRequests['api.v1.subcontractor.claims.certify'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.certify(projectId, id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async uncertifySubcontractorClaim(projectId: Id, id: Id) {
      const [data, error] = await ClaimsService.subcontractor.uncertify(projectId, id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async certifySubcontractorClaimLite(
      id: Id,
      request: ApiRequests['api.v1.subcontractor.claims.lite.certify'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.certifyLite(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async uncertifySubcontractorClaimLite(id: Id) {
      const [data, error] = await ClaimsService.subcontractor.uncertifyLite(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async storeSubcontractorClaimMedia(
      id: Id,
      signedParams: unknown,
      request: ApiRequests['api.v1.subcontractor.claims.media.store'] | FormData,
    ) {
      const [data, error] = await ClaimsService.subcontractor.storeMedia(id, signedParams, request);

      if(error) {
        throw error;
      }

      return data;
    },

    async viewSubcontractorClaimMedia(id: Id, signedParams: unknown) {
      const [data, error] = await ClaimsService.subcontractor.media(id, signedParams);

      if(error) {
        throw error;
      }

      return data;
    },

    async destroySubcontractorClaimMedia(
      id: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.subcontractor.claims.media.destroy'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.destroyMedia(id, signedParams, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async exportSubcontractorClaim(
      projectId: Id,
      claimId: Id,
      form: ApiRequests['api.v1.subcontractor.claims.export'] = {},
    ) {
      const [data, error] = await ClaimsService.subcontractor.export(projectId, claimId, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async issueSubcontractorCertificate(
      projectId: Id,
      claimId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.issue'],
    ) {
      const [data, error] = await ClaimsService.subcontractor.issueCertificate(projectId, claimId, request);

      if(error) {
        throw error;
      }

      return data;
    },

    async massSendSubcontractorClaim(projectId: Id, request: ApiRequests['api.v1.subcontractor.claims.mass-send']) {
      const [data, error] = await ClaimsService.subcontractor.massSend(projectId, request);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async resendPqs(id: Id) {
      const [data, error] = await ClaimsService.resendPqs(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async resendContractor(id: Id) {
      const [data, error] = await ClaimsService.resendContractor(id);

      if(error) {
        throw error;
      }

      return data;
    },
  },
});
